import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight, Loader2, Play } from 'lucide-react';

interface PhotoMetadata {
  slot?: number | string;
  trimStart?: number;
  trimEnd?: number;
  timestamp?: number;
}

interface ProfilePictureModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  images?: string[];
  videoUrls?: string[];
  photoMetadata?: PhotoMetadata[];
  initialIndex?: number;
  userName?: string;
}

export const ProfilePictureModal: React.FC<ProfilePictureModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  images = [],
  videoUrls = [],
  photoMetadata = [],
  initialIndex = 0,
  userName = 'User'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);

  // Pre-process media to find valid items
  const displayImages: string[] = [];
  const displayVideos: string[] = [];
  const validMap: number[] = [];

  const maxLen = Math.max(images.length, videoUrls.length);
  for (let i = 0; i < maxLen; i++) {
    const img = images[i] || '';
    const vid = videoUrls[i] || '';
    if (img.trim().length > 0 || vid.trim().length > 0) {
      displayImages.push(img.trim());
      displayVideos.push(vid.trim());
      validMap.push(i);
    }
  }

  // Fallback
  if (displayImages.length === 0 && (imageUrl || '').trim()) {
      displayImages.push((imageUrl || '').trim());
      displayVideos.push('');
      validMap.push(0);
  }

  useEffect(() => {
    if (isOpen) {
      const mappedIdx = validMap.indexOf(initialIndex);
      setCurrentIndex(mappedIdx !== -1 ? mappedIdx : 0);
      setIsLoading(true);
      setIsVideoPlaying(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, initialIndex, validMap.length]);

  // Safety net: if the current slot has neither an image src nor a video src,
  // the <img>/<video> elements never render, so onLoad/onError never fire and
  // isLoading is stuck at true — the user just sees the spinner forever. Force
  // it off so at least we don't pretend something is loading when it isn't.
  useEffect(() => {
    if (!isOpen) return;
    const img = displayImages[currentIndex];
    const vid = displayVideos[currentIndex];
    if (!img && !vid) setIsLoading(false);
  }, [isOpen, currentIndex, displayImages, displayVideos]);

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (displayImages.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    setIsLoading(true);
    setIsVideoPlaying(false);
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (displayImages.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % displayImages.length);
    setIsLoading(true);
    setIsVideoPlaying(false);
  };

  if (!isOpen || displayImages.length === 0) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          // Inline style: Tailwind silently drops `z-999999999` (arbitrary
          // values must use bracket syntax: `z-[999999999]`). Without a valid
          // z-index, this lightbox sat behind any sibling-portal modal
          // (UserDetailModal at z-[10001]) and was invisible — even though
          // the click handler fired correctly. Inline keeps it bulletproof.
          style={{ zIndex: 2147483647 }}
        >
          {/* Backdrop - Darker & Blurred to show depth */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-xl"
          />

          {/* Close Button - Clean Floating Style */}
          <button
            onClick={onClose}
            className="absolute top-8 right-8 z-1000 w-14 h-14 rounded-full bg-white/5 hover:bg-white/15 backdrop-blur-xl flex items-center justify-center text-white transition-all shadow-2xl active:scale-90 border border-white/10"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Media Container - Unconstrained, keeping natural size but capped to viewport */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="relative w-full h-full flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div 
              className="relative max-w-full max-h-full flex items-center justify-center overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
               {isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                    <Loader2 className="w-12 h-12 text-white/40 animate-spin" />
                  </div>
                )}

                {/* Media Layer */}
                <div className="relative max-w-screen-xl max-h-screen">
                  {(displayImages[currentIndex] || imageUrl) && (
                    <img
                      src={displayImages[currentIndex] || imageUrl}
                      alt={userName}
                      className="max-w-[88vw] max-h-[90vh] object-contain shadow-[0_0_100px_rgba(0,0,0,0.5)] rounded-lg"
                      onLoad={() => setIsLoading(false)}
                      onError={() => setIsLoading(false)}
                      // Cached-image race: if the src is already in the browser
                      // cache, the `load` event fires before React attaches
                      // onLoad, so isLoading sticks at true. Use a ref callback
                      // to read `.complete` right after the element mounts and
                      // catch that case.
                      ref={(el) => {
                        if (el && el.complete && el.naturalWidth > 0) {
                          setIsLoading(false);
                        }
                      }}
                      style={{ opacity: isVideoPlaying ? 0 : 1 }}
                    />
                  )}

                  {displayVideos[currentIndex] && (
                    <video
                      ref={videoRef}
                      src={displayVideos[currentIndex]}
                      className="absolute inset-0 w-full h-full object-contain px-[6vw]"
                      playsInline
                      autoPlay
                      loop
                      muted
                      onPlay={() => {
                        setIsVideoPlaying(true);
                        setIsLoading(false);
                      }}
                      onTimeUpdate={(e) => {
                        const currentOriginalSlot = validMap[currentIndex] ?? currentIndex;
                        const meta = photoMetadata
                          ?.filter(m => Number(m.slot) === Number(currentOriginalSlot))
                          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];
                        
                        if (meta?.trimStart !== undefined && meta?.trimEnd !== undefined) {
                          const vid = e.currentTarget;
                          if (vid.currentTime >= meta.trimEnd || vid.currentTime < meta.trimStart - 0.1) {
                            vid.currentTime = meta.trimStart;
                          }
                        }
                      }}
                    />
                  )}
                </div>

                {/* Video Play Button Overlay (if paused) */}
                {displayVideos[currentIndex] && !isVideoPlaying && !isLoading && (
                   <div 
                     className="absolute inset-0 flex items-center justify-center cursor-pointer"
                     onClick={() => videoRef.current?.play()}
                   >
                     <div className="w-20 h-20 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center border border-white/10">
                       <Play className="w-10 h-10 text-white fill-current opacity-90 ml-1" />
                     </div>
                   </div>
                )}

                {/* Navigation - Minimalist & Edge-aligned */}
                {displayImages.length > 1 && (
                  <>
                    <button 
                      onClick={handlePrev} 
                      className="fixed left-2 sm:left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-sm text-white flex items-center justify-center z-100 transition-all border border-white/5 active:scale-90"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button 
                      onClick={handleNext} 
                      className="fixed right-2 sm:right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-sm text-white flex items-center justify-center z-100 transition-all border border-white/5 active:scale-90"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    
                    {/* Pagination Dots */}
                    <div className="fixed bottom-10 left-0 right-0 flex justify-center z-100">
                      <div className="flex gap-2 bg-black/40 backdrop-blur-xl px-4 py-2 rounded-full border border-white/10">
                        {displayImages.map((_, i) => (
                          <div key={i} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentIndex ? 'bg-white w-6' : 'bg-white/20'}`} />
                        ))}
                      </div>
                    </div>
                  </>
                )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};
